import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/v5/components/ui/dialog";
import { Button } from "@/v5/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/v5/components/ui/accordion";
import {
  fetchHeaderFooterList,
  fetchHeaderFooter,
  fetchContentList,
  fetchContentTemplate,
  fetchContentMostUsed,
  resetContentMostUsed,
  type HeaderFooterSummary,
  type ContentCategory,
  type MostUsedTemplate,
} from "@/v5/hooks/useEmailTemplates";
import { Input } from "@/v5/components/ui/input";
import { ArrowLeft, FileText, Layout, Loader2, Search, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

type Step = "choose" | "headers" | "content";

interface EmailTemplatePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplyHeaderFooter: (header: string, footer: string) => void;
  onApplyContent: (content: string, subject?: string) => void;
  patientId?: string;
  planId?: string;
}

export function EmailTemplatePicker({
  open,
  onOpenChange,
  onApplyHeaderFooter,
  onApplyContent,
  patientId,
  planId,
}: EmailTemplatePickerProps) {
  const [step, setStep] = useState<Step>("choose");
  const [headerList, setHeaderList] = useState<HeaderFooterSummary[]>([]);
  const [categoryList, setCategoryList] = useState<ContentCategory[]>([]);
  const [mostUsed, setMostUsed] = useState<MostUsedTemplate[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState<string | null>(null);

  const q = query.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!q) return [];
    return categoryList
      .map((cat) => ({
        ...cat,
        templates: cat.templates.filter((t) =>
          t.name.toLowerCase().includes(q),
        ),
      }))
      .filter((cat) => cat.templates.length > 0);
  }, [categoryList, q]);

  const reset = useCallback(() => {
    setStep("choose");
    setHeaderList([]);
    setCategoryList([]);
    setMostUsed([]);
    setQuery("");
    setLoading(false);
    setApplying(null);
  }, []);

  const handleResetMostUsed = useCallback(async (id: string) => {
    setMostUsed((prev) => prev.filter((t) => t.id !== id));
    try {
      await resetContentMostUsed(id);
    } catch {
      /* optimistic; will reappear on next open if it failed */
    }
  }, []);

  const handleOpenChange = useCallback(
    (v: boolean) => {
      if (!v) reset();
      onOpenChange(v);
    },
    [onOpenChange, reset],
  );

  const loadHeaders = useCallback(async () => {
    setStep("headers");
    setLoading(true);
    try {
      const list = await fetchHeaderFooterList();
      setHeaderList(list);
    } catch {
      setHeaderList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadContent = useCallback(async () => {
    setStep("content");
    setQuery("");
    setLoading(true);
    fetchContentMostUsed()
      .then(setMostUsed)
      .catch(() => setMostUsed([]));
    try {
      const cats = await fetchContentList();
      setCategoryList(cats);
    } catch {
      setCategoryList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelectHeaderFooter = useCallback(
    async (id: string) => {
      setApplying(id);
      try {
        const detail = await fetchHeaderFooter(id, patientId, planId);
        onApplyHeaderFooter(detail.header, detail.footer);
        handleOpenChange(false);
      } catch {
        /* let user retry */
      } finally {
        setApplying(null);
      }
    },
    [onApplyHeaderFooter, handleOpenChange, patientId, planId],
  );

  const handleSelectContent = useCallback(
    async (id: string) => {
      setApplying(id);
      try {
        const detail = await fetchContentTemplate(id, patientId, planId);
        onApplyContent(detail.content, detail.subject || undefined);
        handleOpenChange(false);
      } catch {
        /* let user retry */
      } finally {
        setApplying(null);
      }
    },
    [onApplyContent, handleOpenChange, patientId, planId],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step !== "choose" && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 -ml-1"
                onClick={() => {
                  setStep("choose");
                  setQuery("");
                }}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            {step === "choose" && "Email Templates"}
            {step === "headers" && "Header / Footer Templates"}
            {step === "content" && "Content Templates"}
          </DialogTitle>
          <DialogDescription>
            {step === "choose" && "Choose a template type to insert into your email"}
            {step === "headers" && "Select a header/footer to wrap your email"}
            {step === "content" && "Select a template to insert into your email body"}
          </DialogDescription>
        </DialogHeader>

        {/* Step: Choose type */}
        {step === "choose" && (
          <div className="grid grid-cols-2 gap-3 py-2">
            <button
              onClick={loadHeaders}
              className="flex flex-col items-center gap-3 rounded-xl border-2 border-border p-6 transition-colors hover:border-primary hover:bg-primary/5"
            >
              <Layout className="h-8 w-8 text-muted-foreground" />
              <div className="text-center">
                <p className="text-sm font-semibold">Header / Footer</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Wrap your email with branded headers and footers
                </p>
              </div>
            </button>
            <button
              onClick={loadContent}
              className="flex flex-col items-center gap-3 rounded-xl border-2 border-border p-6 transition-colors hover:border-primary hover:bg-primary/5"
            >
              <FileText className="h-8 w-8 text-muted-foreground" />
              <div className="text-center">
                <p className="text-sm font-semibold">Content Template</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Insert pre-built email content at the cursor
                </p>
              </div>
            </button>
          </div>
        )}

        {/* Search (content step only) */}
        {step === "content" && (
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search templates..."
              className="pl-9"
            />
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Step: Header/Footer list */}
        {step === "headers" && !loading && (
          <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6">
            {headerList.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No header/footer templates available
              </p>
            ) : (
              <div className="space-y-1.5 pb-2">
                {headerList.map((hf) => (
                  <button
                    key={hf.id}
                    disabled={applying !== null}
                    onClick={() => handleSelectHeaderFooter(hf.id)}
                    className="w-full text-left rounded-lg border border-border px-4 py-3 text-sm font-medium transition-colors hover:bg-muted/60 hover:border-primary/40 disabled:opacity-60 flex items-center justify-between"
                  >
                    <span>{hf.name}</span>
                    {applying === hf.id && (
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step: Content templates — search results */}
        {step === "content" && !loading && q && (
          <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6">
            {searchResults.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No templates match "{query.trim()}"
              </p>
            ) : (
              <div className="space-y-3 pb-2">
                {searchResults.map((cat) => (
                  <div key={cat.id}>
                    <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground py-2">
                      {cat.name}
                    </p>
                    <div className="space-y-1">
                      {cat.templates.map((tpl) => (
                        <button
                          key={tpl.id}
                          disabled={applying !== null}
                          onClick={() => handleSelectContent(tpl.id)}
                          className="w-full text-left rounded-lg bg-muted/40 px-4 py-2.5 text-sm transition-colors hover:bg-primary hover:text-white disabled:opacity-60 flex items-center justify-between"
                        >
                          <span>{tpl.name}</span>
                          {applying === tpl.id && (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step: Content templates (categorized accordion) */}
        {step === "content" && !loading && !q && (
          <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6">
            {mostUsed.length > 0 && (
              <div className="pb-3 mb-2 border-b border-border">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground py-2">
                  Most Used
                </p>
                <div className="space-y-1">
                  {mostUsed.map((tpl) => (
                    <div
                      key={tpl.id}
                      className="group w-full rounded-lg bg-muted/40 text-sm transition-colors hover:bg-primary hover:text-white flex items-center"
                    >
                      <button
                        disabled={applying !== null}
                        onClick={() => handleSelectContent(tpl.id)}
                        className="flex-1 text-left px-4 py-2.5 disabled:opacity-60 flex items-center justify-between min-w-0"
                      >
                        <span className="truncate">{tpl.name}</span>
                        {applying === tpl.id && (
                          <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                        )}
                      </button>
                      <button
                        type="button"
                        aria-label={`Remove ${tpl.name} from most used`}
                        title="Remove from Most Used"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResetMostUsed(tpl.id);
                        }}
                        className="px-3 py-2.5 text-muted-foreground group-hover:text-white/80 hover:!text-white"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {categoryList.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No content templates available
              </p>
            ) : (
              <Accordion type="multiple" className="pb-2">
                {categoryList.map((cat) => (
                  <AccordionItem key={cat.id} value={cat.id}>
                    <AccordionTrigger className="text-xs font-bold uppercase tracking-wide text-muted-foreground hover:no-underline">
                      {cat.name}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-1">
                        {cat.templates.map((tpl) => (
                          <button
                            key={tpl.id}
                            disabled={applying !== null}
                            onClick={() => handleSelectContent(tpl.id)}
                            className="w-full text-left rounded-lg bg-muted/40 px-4 py-2.5 text-sm transition-colors hover:bg-primary hover:text-white disabled:opacity-60 flex items-center justify-between"
                          >
                            <span>{tpl.name}</span>
                            {applying === tpl.id && (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            )}
                          </button>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
